import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const aud = (n) => `A$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const GoalForm = ({ initial, onSaved, onCancel }) => {
  const [v, setV] = useState(initial ?? "");
  const save = async () => {
    try { const { data } = await api.put("/sales-goal", { goal_aud: Number(v) }); onSaved(data); toast.success("Monthly goal saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Input data-testid="sales-goal-input" type="number" min="1" value={v} onChange={(e) => setV(e.target.value)} placeholder="Monthly goal, A$" className="h-10 w-44 bg-white rounded-none" />
      <Button data-testid="sales-goal-save" onClick={save} disabled={!v} className="h-10 rounded-none">Save goal</Button>
      {onCancel && <button data-testid="sales-goal-cancel" onClick={onCancel} className="text-sm text-[#8C827A] hover:text-[#3B1B48]">Cancel</button>}
    </div>
  );
};

const Fact = ({ label, value, testId }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-[#8C827A]">{label}</p><p data-testid={testId} className="font-display text-xl text-[#3B1B48] mt-1">{value}</p></div>
);

export const SalesGoal = () => {
  const [g, setG] = useState(null);
  const [edit, setEdit] = useState(false);
  useEffect(() => { api.get("/sales-goal").then((r) => setG(r.data)).catch(() => {}); }, []);
  if (!g) return null;
  const saved = (d) => { setG(d); setEdit(false); };
  const pct = Math.min(100, g.pct || 0);
  const ahead = g.pct != null && g.pct >= g.expected_pct;
  return (
    <div data-testid="sales-goal-card" className="rise bg-[#F4EFE6] border border-[#E2D9CC] p-6 sm:p-8 mb-16">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <p className="eyebrow flex items-center gap-2"><Target className="w-3.5 h-3.5" />Sales goal · {g.month}</p>
          <p className="font-display text-3xl sm:text-4xl text-[#3B1B48] mt-2">
            <span data-testid="sales-goal-revenue">{aud(g.revenue_aud)}</span>
            {g.goal_aud && <span className="text-[#8C827A] text-xl sm:text-2xl"> of <span data-testid="sales-goal-target">{aud(g.goal_aud)}</span></span>}
          </p>
          <p data-testid="sales-goal-split" className="text-xs text-[#8C827A] mt-1">Small towns {aud(g.town_aud)} · Relocation guides {aud(g.relocation_aud)}</p>
        </div>
        {g.goal_aud && !edit && <button data-testid="sales-goal-edit" onClick={() => setEdit(true)} className="flex items-center gap-1.5 text-sm text-[#3B1B48] hover:text-[#C59B27] transition-colors"><Pencil className="w-3.5 h-3.5" />Change goal</button>}
      </div>
      {(!g.goal_aud || edit) ? (
        <div className="space-y-3">
          {!g.goal_aud && <p className="text-sm text-[#8C827A]">Set a monthly revenue goal. It carries over each month until you change it.</p>}
          <GoalForm initial={g.goal_aud} onSaved={saved} onCancel={g.goal_aud ? () => setEdit(false) : null} />
        </div>
      ) : (
        <>
          <div className="relative h-3 bg-[#E2D9CC] mb-2" data-testid="sales-goal-bar">
            <div className="absolute inset-y-0 left-0 bg-[#C59B27] transition-[width] duration-700" style={{ width: `${pct}%` }} />
            <div className="absolute -top-1 -bottom-1 w-px bg-[#3B1B48]" style={{ left: `${Math.min(100, g.expected_pct)}%` }} title="Where you'd be at an even pace" />
          </div>
          <p data-testid="sales-goal-pct" className={`text-sm mb-6 ${ahead ? "text-[#2D6A4F]" : "text-[#B45309]"}`}>
            {g.pct}% of goal · {g.pct >= 100 ? "goal reached" : ahead ? "ahead of pace" : "behind pace"} (an even pace would be {Math.round(g.expected_pct)}% by today)
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            <Fact label="Days left" value={g.days_left} testId="sales-goal-days-left" />
            <Fact label="Needed per day" value={g.remaining_aud > 0 ? aud(g.pace_needed_aud) : "—"} testId="sales-goal-pace" />
            <Fact label="Orders this month" value={g.orders} testId="sales-goal-orders" />
            <Fact label={`Last month (${g.last_month.month.split(" ")[0]})`} value={`${aud(g.last_month.revenue_aud)}${g.last_month.pct != null ? ` · ${Math.round(g.last_month.pct)}%` : ""}`} testId="sales-goal-last-month" />
          </div>
        </>
      )}
    </div>
  );
};
